import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Settings, Music,
  X, ChevronUp, ChevronDown, ChevronLeft, Loader2, BookOpen, Link2, ListPlus,
} from 'lucide-react';
import * as api from '../../lib/api';

const formatTime = (seconds = 0) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// "Cute color options" — a small preset palette, Sirati brand colors plus a
// few playful extras. Themes only apply to this custom player.
export const AUDIO_PLAYER_THEMES = {
  midnight: {
    label: 'Midnight', swatch: '#111111',
    cardBg: 'rgba(17,17,17,0.6)', controlsBg: 'rgba(17,17,17,0.6)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.7)',
    sliderTrack: 'rgba(255,255,255,0.2)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.15)',
  },
  teal: {
    label: 'Teal Dream', swatch: '#4AADA8',
    cardBg: 'linear-gradient(135deg, #4AADA8 0%, #2E7A76 100%)', controlsBg: 'rgba(0,0,0,0.18)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.75)',
    sliderTrack: 'rgba(255,255,255,0.25)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.2)',
  },
  lavender: {
    label: 'Lavender Bloom', swatch: '#A99CC8',
    cardBg: 'linear-gradient(135deg, #A99CC8 0%, #7C6DA3 100%)', controlsBg: 'rgba(0,0,0,0.18)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.75)',
    sliderTrack: 'rgba(255,255,255,0.25)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.2)',
  },
  blush: {
    label: 'Blush', swatch: '#F2A9C4',
    cardBg: 'linear-gradient(135deg, #F6C6D9 0%, #E888AE 100%)', controlsBg: 'rgba(0,0,0,0.15)',
    text: '#4A2233', mutedText: 'rgba(74,34,51,0.7)',
    sliderTrack: 'rgba(74,34,51,0.2)', sliderFill: '#4A2233', iconHoverBg: 'rgba(74,34,51,0.12)',
  },
  sunset: {
    label: 'Sunset', swatch: '#F0A16C',
    cardBg: 'linear-gradient(135deg, #F6B26B 0%, #E8768C 55%, #A99CC8 100%)', controlsBg: 'rgba(0,0,0,0.18)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.8)',
    sliderTrack: 'rgba(255,255,255,0.25)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.2)',
  },
  ocean: {
    label: 'Ocean', swatch: '#5FB8D6',
    cardBg: 'linear-gradient(135deg, #6FD0E0 0%, #4A7FC7 100%)', controlsBg: 'rgba(0,0,0,0.18)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.8)',
    sliderTrack: 'rgba(255,255,255,0.25)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.2)',
  },
  glass: {
    label: 'Glass', swatch: '#ffffff',
    cardBg: 'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.15) 100%)',
    controlsBg: 'rgba(255,255,255,0.25)',
    text: '#ffffff', mutedText: 'rgba(255,255,255,0.8)',
    sliderTrack: 'rgba(255,255,255,0.3)', sliderFill: '#ffffff', iconHoverBg: 'rgba(255,255,255,0.3)',
    blur: 'backdrop-blur-xl', border: '1px solid rgba(255,255,255,0.4)',
  },
};

const getTheme = (key) => AUDIO_PLAYER_THEMES[key] || AUDIO_PLAYER_THEMES.midnight;

// Normalizes config so an older single-track save (audio_url/title at the
// top level) still plays as a one-track playlist.
const getTracks = (config) => {
  if (Array.isArray(config.tracks) && config.tracks.length > 0) return config.tracks;
  if (config.audio_url) return [{ audio_url: config.audio_url, title: config.title || '' }];
  return [];
};

const CustomSlider = ({ value, onChange, theme }) => (
  <div
    className="relative w-full h-1 rounded-full cursor-pointer"
    style={{ background: theme.sliderTrack }}
    onClick={(e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      onChange(Math.min(Math.max(percentage, 0), 100));
    }}
  >
    <motion.div
      className="absolute top-0 left-0 h-full rounded-full"
      style={{ background: theme.sliderFill, width: `${value}%` }}
      animate={{ width: `${value}%` }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    />
  </div>
);

const ControlButton = ({ children, onClick, active, disabled, theme, label }) => (
  <motion.button
    whileHover={disabled ? {} : { scale: 1.1 }}
    whileTap={disabled ? {} : { scale: 0.9 }}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(); }}
    disabled={disabled}
    aria-label={label}
    className="h-8 w-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
    style={{ color: theme.text, background: active ? theme.iconHoverBg : 'transparent' }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = theme.iconHoverBg; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = active ? theme.iconHoverBg : 'transparent'; }}
  >
    {children}
  </motion.button>
);

/* ── Quran reciter/surah picker ── */
const QuranPicker = ({ onPick, onBack }) => {
  const [reciters, setReciters] = useState(null);
  const [selectedReciter, setSelectedReciter] = useState(null);
  const [surahs, setSurahs] = useState(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.getQuranReciters().then(r => setReciters(r.data || [])).catch(() => setError('Could not load reciters.'));
  }, []);

  useEffect(() => {
    if (!selectedReciter) return;
    setSurahs(null);
    api.getQuranSurahsForReciter(selectedReciter.moshaf_id)
      .then(r => setSurahs(r.data || []))
      .catch(() => setError('Could not load surahs for this reciter.'));
  }, [selectedReciter]);

  const filteredReciters = useMemo(() => {
    if (!reciters) return [];
    const q = query.trim().toLowerCase();
    if (!q) return reciters;
    return reciters.filter(r => r.reciter_name.toLowerCase().includes(q));
  }, [reciters, query]);

  if (error) {
    return <p className="text-xs text-red-500 text-center py-6">{error}</p>;
  }

  if (selectedReciter) {
    return (
      <div>
        <button onClick={() => setSelectedReciter(null)} className="flex items-center gap-1 text-xs text-text-muted hover:text-primary mb-2">
          <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" /> Reciters
        </button>
        <p className="text-xs font-medium text-text-main mb-2">{selectedReciter.reciter_name}</p>
        {!surahs ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" /></div>
        ) : (
          <>
            <button
              onClick={() => onPick(surahs.map(s => ({ audio_url: s.audio_url, title: s.title })))}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 mb-1.5 rounded-arch text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              data-testid="quran-add-all-btn"
            >
              <ListPlus className="w-3.5 h-3.5" aria-hidden="true" /> Play All {surahs.length} Surahs
            </button>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {surahs.map(s => (
                <button
                  key={s.surah_number}
                  onClick={() => onPick({ audio_url: s.audio_url, title: s.title })}
                  className="w-full text-left px-3 py-1.5 rounded-arch text-xs text-text-main hover:bg-primary/10 transition-colors"
                  data-testid={`quran-surah-${s.surah_number}`}
                >
                  {s.surah_number}. {s.title}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-text-muted hover:text-primary mb-2">
        <ChevronLeft className="w-3.5 h-3.5" aria-hidden="true" /> Back
      </button>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search reciters…"
        className="w-full px-3 py-1.5 mb-2 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800"
      />
      {!reciters ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" /></div>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-1">
          {filteredReciters.map(r => (
            <button
              key={r.moshaf_id}
              onClick={() => setSelectedReciter(r)}
              className="w-full text-left px-3 py-1.5 rounded-arch text-xs text-text-main hover:bg-primary/10 transition-colors"
              data-testid={`quran-reciter-${r.moshaf_id}`}
            >
              {r.reciter_name} <span className="text-text-muted">— {r.style}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Settings — playlist builder + theme picker ── */
const AudioPlayerSettings = ({ config, onSave, onCancel }) => {
  const [tracks, setTracks] = useState(getTracks(config));
  const [coverUrl, setCoverUrl] = useState(config.cover_url || '');
  const [playlistTitle, setPlaylistTitle] = useState(config.title || '');
  const [theme, setTheme] = useState(config.theme || 'midnight');
  const [addMode, setAddMode] = useState(null); // null | 'link' | 'quran'
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  const addTrack = (trackOrTracks) => {
    const toAdd = Array.isArray(trackOrTracks) ? trackOrTracks : [trackOrTracks];
    setTracks(t => [...t, ...toAdd]);
    setAddMode(null);
    setLinkUrl('');
    setLinkTitle('');
  };
  const removeTrack = (i) => setTracks(t => t.filter((_, idx) => idx !== i));
  const moveTrack = (i, dir) => {
    setTracks(t => {
      const next = [...t];
      const j = i + dir;
      if (j < 0 || j >= next.length) return t;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  return (
    <div className="w-[280px] rounded-3xl p-4 bg-white dark:bg-gray-800 shadow-2xl border border-white/10" data-testid="audio-widget-settings">
      <h4 className="font-heading text-sm font-semibold text-text-main mb-3">Audio Player Settings</h4>

      <div className="space-y-3">
        <div>
          <label className="block text-[11px] text-text-muted mb-1" htmlFor="playlist-title-input">Playlist name (optional)</label>
          <input
            id="playlist-title-input"
            type="text"
            value={playlistTitle}
            onChange={(e) => setPlaylistTitle(e.target.value.slice(0, 60))}
            placeholder="My favorites"
            className="w-full px-3 py-2 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800"
          />
        </div>
        <div>
          <label className="block text-[11px] text-text-muted mb-1" htmlFor="cover-url-input">Cover image URL (optional)</label>
          <input
            id="cover-url-input"
            type="text"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder="https://…/cover.jpg"
            className="w-full px-3 py-2 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800"
          />
        </div>

        {/* Track list */}
        <div>
          <p className="block text-[11px] text-text-muted mb-1.5">Tracks ({tracks.length})</p>
          {tracks.length > 0 && (
            <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
              {tracks.map((t, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-arch bg-black/5 dark:bg-white/10">
                  <span className="flex-1 text-xs text-text-main truncate">{t.title || t.audio_url}</span>
                  <button onClick={() => moveTrack(i, -1)} disabled={i === 0} className="p-0.5 text-text-muted hover:text-primary disabled:opacity-20" aria-label="Move up">
                    <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button onClick={() => moveTrack(i, 1)} disabled={i === tracks.length - 1} className="p-0.5 text-text-muted hover:text-primary disabled:opacity-20" aria-label="Move down">
                    <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                  <button onClick={() => removeTrack(i)} className="p-0.5 text-red-400 hover:text-red-500" aria-label="Remove track">
                    <X className="w-3.5 h-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {addMode === 'link' && (
            <div className="p-2 rounded-arch bg-black/5 dark:bg-white/10 mb-2 space-y-1.5">
              <input type="text" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…/track.mp3"
                className="w-full px-2.5 py-1.5 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800" />
              <input type="text" value={linkTitle} onChange={(e) => setLinkTitle(e.target.value.slice(0, 60))} placeholder="Track title (optional)"
                className="w-full px-2.5 py-1.5 rounded-arch text-xs border border-primary/20 bg-white/70 dark:bg-white dark:text-gray-800" />
              <div className="flex gap-1.5">
                <button onClick={() => setAddMode(null)} className="flex-1 py-1.5 rounded-arch text-[11px] text-text-muted">Cancel</button>
                <button
                  onClick={() => linkUrl.trim() && addTrack({ audio_url: linkUrl.trim(), title: linkTitle.trim() })}
                  disabled={!linkUrl.trim()}
                  className="flex-1 py-1.5 rounded-arch text-[11px] font-medium bg-primary text-white disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {addMode === 'quran' && (
            <div className="p-2 rounded-arch bg-black/5 dark:bg-white/10 mb-2">
              <QuranPicker onPick={addTrack} onBack={() => setAddMode(null)} />
            </div>
          )}

          {!addMode && (
            <div className="flex gap-1.5">
              <button onClick={() => setAddMode('link')}
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-arch text-[11px] font-medium border border-primary/20 text-text-main hover:bg-primary/5 transition-colors"
                data-testid="add-track-link-btn">
                <Link2 className="w-3.5 h-3.5" aria-hidden="true" /> Paste link
              </button>
              <button onClick={() => setAddMode('quran')}
                className="flex-1 flex items-center justify-center gap-1 py-2 rounded-arch text-[11px] font-medium border border-primary/20 text-text-main hover:bg-primary/5 transition-colors"
                data-testid="add-track-quran-btn">
                <BookOpen className="w-3.5 h-3.5" aria-hidden="true" /> Quran
              </button>
            </div>
          )}
        </div>

        <div>
          <p className="block text-[11px] text-text-muted mb-1.5">Color</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(AUDIO_PLAYER_THEMES).map(([key, t]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTheme(key)}
                title={t.label}
                aria-label={t.label}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${theme === key ? 'scale-110 border-primary' : 'border-black/10 dark:border-white/20'}`}
                style={{ background: t.cardBg }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button onClick={onCancel} className="flex-1 py-2 rounded-arch text-xs font-medium text-text-muted hover:bg-black/5 dark:hover:bg-white/10 transition-colors">
          Cancel
        </button>
        <button
          onClick={() => onSave({ tracks, cover_url: coverUrl.trim(), title: playlistTitle.trim(), theme })}
          disabled={tracks.length === 0}
          className="flex-1 py-2 rounded-arch text-xs font-medium bg-primary text-white disabled:opacity-50"
          data-testid="audio-widget-save-btn"
        >
          Save
        </button>
      </div>
    </div>
  );
};

const AudioPlayerWidget = ({ config = {}, editMode, onUpdateConfig }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);

  const tracks = getTracks(config);
  const { cover_url: cover, title: playlistTitle, theme: themeKey } = config;
  const theme = getTheme(themeKey);
  const current = tracks[trackIndex] || null;

  // Reset to the first track whenever the playlist itself changes
  useEffect(() => { setTrackIndex(0); setIsPlaying(false); }, [config.tracks, config.audio_url]);

  const goTo = (index, autoplay) => {
    setTrackIndex(index);
    setProgress(0);
    setCurrentTime(0);
    if (autoplay) {
      requestAnimationFrame(() => audioRef.current?.play());
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  const goNext = (autoplay = true) => {
    if (tracks.length <= 1) return;
    const next = isShuffle
      ? Math.floor(Math.random() * tracks.length)
      : (trackIndex + 1) % tracks.length;
    goTo(next, autoplay);
  };

  const goPrev = () => {
    if (tracks.length <= 1) return;
    goTo((trackIndex - 1 + tracks.length) % tracks.length, isPlaying);
  };

  const handleEnded = () => {
    if (isRepeat) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      return;
    }
    if (trackIndex === tracks.length - 1 && !isShuffle) {
      setIsPlaying(false); // end of playlist
      return;
    }
    goNext(true);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setProgress(isFinite(pct) ? pct : 0);
    setCurrentTime(audioRef.current.currentTime);
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (value) => {
    if (audioRef.current && audioRef.current.duration) {
      const time = (value / 100) * audioRef.current.duration;
      if (isFinite(time)) {
        audioRef.current.currentTime = time;
        setProgress(value);
      }
    }
  };

  // No tracks configured yet
  if (tracks.length === 0) {
    if (!editMode) return null;
    return showSettings ? (
      <AudioPlayerSettings config={config} onCancel={() => setShowSettings(false)}
        onSave={(next) => { onUpdateConfig(next); setShowSettings(false); }} />
    ) : (
      <button
        onClick={() => setShowSettings(true)}
        className="w-[280px] rounded-3xl p-8 flex flex-col items-center gap-2 border-2 border-dashed border-white/30 text-white/70 hover:text-white hover:border-white/50 transition-colors"
        data-testid="audio-widget-empty"
      >
        <Music className="w-6 h-6" aria-hidden="true" />
        <span className="text-xs font-medium">Add music or a Quran playlist</span>
      </button>
    );
  }

  if (showSettings) {
    return (
      <AudioPlayerSettings config={config} onCancel={() => setShowSettings(false)}
        onSave={(next) => { onUpdateConfig(next); setShowSettings(false); }} />
    );
  }

  return (
    <motion.div
      className={`relative flex flex-col mx-auto rounded-3xl overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.2)] ${theme.blur || 'backdrop-blur-sm'} p-3 w-[280px]`}
      style={{ background: theme.cardBg, border: theme.border }}
      initial={{ opacity: 0, filter: 'blur(10px)' }}
      animate={{ opacity: 1, filter: 'blur(0px)' }}
      transition={{ duration: 0.3, ease: 'easeInOut', type: 'spring' }}
      data-testid="audio-player-widget"
    >
      <audio ref={audioRef} onTimeUpdate={handleTimeUpdate} onEnded={handleEnded} src={current?.audio_url} className="hidden" />

      {editMode && (
        <button
          onClick={() => setShowSettings(true)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
          style={{ color: theme.text }}
          aria-label="Edit audio player"
          data-testid="audio-widget-edit-btn"
        >
          <Settings className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}

      {cover && (
        <div className="bg-white/20 overflow-hidden rounded-2xl h-[180px] w-full relative">
          <img src={cover} alt="cover" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="flex flex-col w-full gap-y-2">
        {playlistTitle && (
          <p className="text-[10px] uppercase tracking-wide text-center opacity-70 -mb-1" style={{ color: theme.text }}>{playlistTitle}</p>
        )}
        <h3 className="font-bold text-base text-center mt-1 truncate px-2" style={{ color: theme.text }}>
          {current?.title || 'Untitled track'}
        </h3>
        {tracks.length > 1 && (
          <p className="text-[10px] text-center -mt-1.5" style={{ color: theme.mutedText }}>
            Track {trackIndex + 1} of {tracks.length}
          </p>
        )}

        <div className="flex flex-col gap-y-1">
          <CustomSlider value={progress} onChange={handleSeek} theme={theme} />
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: theme.text }}>{formatTime(currentTime)}</span>
            <span className="text-sm" style={{ color: theme.text }}>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="flex items-center justify-center w-full">
          <div className="flex items-center gap-2 w-fit rounded-2xl p-2" style={{ background: theme.controlsBg }}>
            <ControlButton onClick={() => setIsShuffle(s => !s)} active={isShuffle} theme={theme} label="Shuffle" disabled={tracks.length <= 1}>
              <Shuffle className="h-5 w-5" aria-hidden="true" />
            </ControlButton>
            <ControlButton onClick={goPrev} theme={theme} label="Previous track" disabled={tracks.length <= 1}>
              <SkipBack className="h-5 w-5" aria-hidden="true" />
            </ControlButton>
            <ControlButton onClick={togglePlay} theme={theme} label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <Pause className="h-5 w-5" aria-hidden="true" /> : <Play className="h-5 w-5" aria-hidden="true" />}
            </ControlButton>
            <ControlButton onClick={() => goNext(true)} theme={theme} label="Next track" disabled={tracks.length <= 1}>
              <SkipForward className="h-5 w-5" aria-hidden="true" />
            </ControlButton>
            <ControlButton onClick={() => setIsRepeat(r => !r)} active={isRepeat} theme={theme} label="Repeat">
              <Repeat className="h-5 w-5" aria-hidden="true" />
            </ControlButton>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AudioPlayerWidget;
